import { Component, h } from 'preact'
import Header from '../header'
import ButtonControl from '../button-control'
import Modal from '../../components/reactModal'
import AlertComponent from '../ui-components/AlertComponent'
import AccordionPanel from '../panel/AccordionPanel'
import { KeywordBlockerPanelBody, KeywordBlockerPanelHeader } from './KeywordBlockerPanel'
import axios from 'axios'
import { ModeratorApprovalPanelBody, ModeratorApprovalPanelHeader } from './ModeratorApprovalPanel'

interface Props {
    keywordBlockerSetting: boolean,
    blockedKeywords: Array<string>,
    moderatorApprovalSetting: boolean
}
interface State {
    storedState: Props,
    isKeywordBlockerEnabled: boolean,
    blockedWords: Array<string>,
    showKeywordBlockerActionButtons: boolean,
    showValidationError: boolean,

    isModeratorApprovalEnabled: boolean,
    showModeratorApprovalActionButtons: boolean,

    hasUnsavedChanges: boolean,
    showUnsavedChangesModal: boolean,

    activePanel?: number|undefined,
    messageType?: string|null,
    message?: string,
}

class PremoderationRules extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      storedState: {
        keywordBlockerSetting: this.props.keywordBlockerSetting,
        blockedKeywords: this.props.blockedKeywords,
        moderatorApprovalSetting: this.props.moderatorApprovalSetting
      },
      isKeywordBlockerEnabled: this.props.keywordBlockerSetting,
      blockedWords: this.props.blockedKeywords,
      showKeywordBlockerActionButtons: false,
      isModeratorApprovalEnabled: this.props.moderatorApprovalSetting,
      showModeratorApprovalActionButtons: false,
      showValidationError: false,
      hasUnsavedChanges: false,
      showUnsavedChangesModal: false
    }

    this.onKeywordBlockerSettingChanged = this.onKeywordBlockerSettingChanged.bind(this)
    this.onBlockedWordsChanged = this.onBlockedWordsChanged.bind(this)
    this.onFocusBlockedWords = this.onFocusBlockedWords.bind(this)
    this.resetKeywordBlockerSettings = this.resetKeywordBlockerSettings.bind(this)
    this.saveKeywordBlockerSetting = this.saveKeywordBlockerSetting.bind(this)
    this.onModeratorApprovalChanged = this.onModeratorApprovalChanged.bind(this)
    this.saveModeratorApprovalSetting = this.saveModeratorApprovalSetting.bind(this)
    this.resetModeratorApprovalSetting = this.resetModeratorApprovalSetting.bind(this)
    this.closeModal = this.closeModal.bind(this)
  }

  componentDidMount () {
    window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => this.onUnload(e))
  }

  componentWillUnmount () {
    window.removeEventListener('beforeunload', this.onUnload)
  }

  onUnload (e: BeforeUnloadEvent) {
    if (this.checkPanelHasUnsavedChanges()) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  onKeywordBlockerSettingChanged (value: boolean) {
    const { keywordBlockerSetting, blockedKeywords } = this.state.storedState
    let showActionButtons = keywordBlockerSetting !== value || blockedKeywords !== this.state.blockedWords
    if (!value) {
      this.setState({ showValidationError: false })
    }
    this.setState({
      isKeywordBlockerEnabled: value,
      showKeywordBlockerActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      activePanel: 0
    })
  }

  onBlockedWordsChanged (value: string) {
    if (value === '') {
      this.setState({
        blockedWords: [],
        showValidationError: true,
        hasUnsavedChanges: true,
        showKeywordBlockerActionButtons: true
      })
      return
    }

    let words = value.split(',').map(word => word.trim())
    let showActionButtons = this.state.storedState.blockedKeywords !== words

    this.setState({
      blockedWords: words,
      showKeywordBlockerActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      showValidationError: false,
      activePanel: 0
    }
    )
  }

  onFocusBlockedWords () {
    this.setState({
      showKeywordBlockerActionButtons: true,
      showValidationError: false
    })
  }

  async saveKeywordBlockerSetting () {
    if (this.state.blockedWords?.length === 0 && this.state.isKeywordBlockerEnabled === true) {
      this.setState({ showValidationError: true })
      return
    }
    this.setState({ messageType: null })
    const successMessage = `'Keyword blocker' settings have been updated successfully.`
    const errorMessage = 'Something went wrong, settings can\'t be updated. Please try again.'

    try {
      await axios.post('/settings/premoderation/keywordBlocker',
        {
          isKeywordBlockerEnabled: this.state.isKeywordBlockerEnabled,
          blockedWords: this.state.blockedWords
        })
      const updatedState = this.state.storedState
      updatedState.blockedKeywords = this.state.blockedWords
      updatedState.keywordBlockerSetting = this.state.isKeywordBlockerEnabled
      this.setState({
        storedState: updatedState,
        message: successMessage,
        messageType: 'success',
        showKeywordBlockerActionButtons: false
      })
    } catch (e) {
      this.setState({
        messageType: 'error',
        message: errorMessage,
        showKeywordBlockerActionButtons: false
      })
    }
  }

  resetKeywordBlockerSettings () {
    const { keywordBlockerSetting, blockedKeywords } = this.state.storedState
    this.setState({
      isKeywordBlockerEnabled: keywordBlockerSetting,
      blockedWords: blockedKeywords,
      showKeywordBlockerActionButtons: false,
      showValidationError: false
    })
  }

  onModeratorApprovalChanged (value: boolean) {
    let showActionButtons = this.state.storedState.moderatorApprovalSetting !== value
    this.setState({
      isModeratorApprovalEnabled: value,
      showModeratorApprovalActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      activePanel: 1
    })
  }

  async saveModeratorApprovalSetting () {
    this.setState({ messageType: null })
    const successMessage = `'Moderator approval' setting has been updated successfully.`
    const errorMessage = 'Something went wrong, setting can\'t be updated. Please try again.'
    try {
      await axios.post('/settings/premoderation/moderatorApproval', {
        isModeratorApprovalEnabled: this.state.isModeratorApprovalEnabled
      })
      const updatedState = this.state.storedState
      updatedState.moderatorApprovalSetting = this.state.isModeratorApprovalEnabled

      this.setState({
        storedState: updatedState,
        message: successMessage,
        messageType: 'success',
        showModeratorApprovalActionButtons: false
      })
    } catch (e) {
      this.setState({
        messageType: 'error',
        message: errorMessage,
        showModeratorApprovalActionButtons: false
      })
    }
  }

  resetModeratorApprovalSetting () {
    this.setState({
      isModeratorApprovalEnabled: this.state.storedState.moderatorApprovalSetting,
      showModeratorApprovalActionButtons: false
    }
    )
  }

  closeModal () {
    this.setState({ showUnsavedChangesModal: false })
  }

  resetChanges () {
    const { keywordBlockerSetting, blockedKeywords, moderatorApprovalSetting } = this.state.storedState
    const hasKeywordBlockerSettingChanged = keywordBlockerSetting !== this.state.isKeywordBlockerEnabled
    const hasBlockedKeywordsChanged = blockedKeywords !== this.state.blockedWords
    const keywordBlockerSettingsChanged = hasKeywordBlockerSettingChanged || hasBlockedKeywordsChanged

    if (keywordBlockerSettingsChanged) {
      this.setState({ activePanel: 1 })
      this.resetKeywordBlockerSettings()
    }
    if (moderatorApprovalSetting !== this.state.isModeratorApprovalEnabled) {
      this.setState({ activePanel: 0 })
      this.resetModeratorApprovalSetting()
    }
    this.setState({ hasUnsavedChanges: false, showUnsavedChangesModal: false })
  }

  checkPanelHasUnsavedChanges () {
    const { keywordBlockerSetting, blockedKeywords, moderatorApprovalSetting } = this.state.storedState
    const hasKeywordBlockerSettingChanged = keywordBlockerSetting !== this.state.isKeywordBlockerEnabled
    const hasBlockedKeywordsChanged = blockedKeywords !== this.state.blockedWords
    const hasModeratorApprovalSettingChanged = moderatorApprovalSetting !== this.state.isModeratorApprovalEnabled

    return hasKeywordBlockerSettingChanged || hasBlockedKeywordsChanged || hasModeratorApprovalSettingChanged
  }

  render ({ keywordBlockerSetting, blockedKeywords }: Props, { storedState, isKeywordBlockerEnabled, blockedWords, showKeywordBlockerActionButtons, showValidationError, message, messageType, showUnsavedChangesModal, isModeratorApprovalEnabled, showModeratorApprovalActionButtons, activePanel } : State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false
    const keywordBlockerStatusText = storedState.keywordBlockerSetting ? 'on' : 'off'
    const moderatorApprovalStatusText = storedState.moderatorApprovalSetting ? 'on' : 'off'
    const panelHasUnsavedChanges = this.checkPanelHasUnsavedChanges()

    const KeywordBlockerPanel = {
      title: <KeywordBlockerPanelHeader statusText={keywordBlockerStatusText} />,
      body: <KeywordBlockerPanelBody
        isKeywordBlockerEnabled={isKeywordBlockerEnabled}
        blockedWords={blockedWords}
        showActionButtons={showKeywordBlockerActionButtons}
        showValidationError={showValidationError}
        onSave={() => this.saveKeywordBlockerSetting()}
        onCancel={() => this.resetKeywordBlockerSettings()}
        onTextareaFocus={() => this.onFocusBlockedWords()}
        onKeywordBlockerSettingChanged={() => this.onKeywordBlockerSettingChanged(!isKeywordBlockerEnabled)}
        onBlockedWordsChanged={(value: string) => this.onBlockedWordsChanged(value.trim())} />,
      hasUnsavedChanges: panelHasUnsavedChanges
    }
    const ModeratorApprovalPanel = {
      title: <ModeratorApprovalPanelHeader statusText={moderatorApprovalStatusText} />,
      body: <ModeratorApprovalPanelBody isModeratorApprovalEnabled={isModeratorApprovalEnabled}
        onModeratorApprovalChanged={() => this.onModeratorApprovalChanged(!isModeratorApprovalEnabled)}
        showActionButtons={showModeratorApprovalActionButtons}
        onSave={() => this.saveModeratorApprovalSetting()}
        onCancel={() => this.resetModeratorApprovalSetting()} />,
      hasUnsavedChanges: panelHasUnsavedChanges
    }
    return (
      <div className='premoderation-rules-overview qa-premoderation-rules-overview'>
        <Header
          title='Premoderation Rules'
          disableCancelButton
          disableSaveButton
        />
        {
          messageType ? <AlertComponent messageType={messageType} message={message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
        }
        <AccordionPanel
          defaultActivePanelIndex={activePanel}
          panels={[KeywordBlockerPanel, ModeratorApprovalPanel]}
          renderUnsavedChangesModal={() => this.setState({ showUnsavedChangesModal: true })}
        />
        <Modal isOpen={showUnsavedChangesModal} onClose={() => this.closeModal()}>
          <div className='unsaved-changes-modal'>
            <h3 className='modal-title'>Unsaved Changes</h3>
            <div className='modal-description'>You have unsaved changes. Do you want to discard them?</div>
            <ButtonControl size='medium' variant='brand' newStyle title='Discard changes'
              onClick={() => this.resetChanges()} />
            <ButtonControl newStyle size='medium' title='Cancel' onClick={() => this.closeModal()} />
          </div>
        </Modal>
      </div>
    )
  }
}
export default PremoderationRules
